import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  EyeOff,
  FileText,
  KeyRound,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import "./manil-review.css";

const DESTINATIONS = Object.freeze({
  workspace: "/ukdi-manil-g1-review",
});

function normalizeDestination(value) {
  return Object.prototype.hasOwnProperty.call(DESTINATIONS, value)
    ? value
    : "workspace";
}

function AccessCard({ icon: Icon, title, children }) {
  return (
    <article className="manil-access-card">
      <span className="manil-icon-box" aria-hidden="true">
        <Icon size={20} strokeWidth={1.8} />
      </span>
      <h2>{title}</h2>
      <p>{children}</p>
    </article>
  );
}

export default function MANILG1ReviewerGateway() {
  const location = useLocation();
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState("");
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");

  const destination = useMemo(() => {
    const requested = new URLSearchParams(location.search).get("next");
    return normalizeDestination(requested);
  }, [location.search]);

  const openDestination = (next) => {
    const target = DESTINATIONS[normalizeDestination(next)];
    navigate(target, { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    const trimmedPasscode = passcode.trim();

    if (!trimmedPasscode) {
      setError("Enter the reviewer passcode supplied by SALAR Technologies.");
      return;
    }

    setError("");
    setStatus("submitting");

    try {
      const response = await fetch("/api/manil-g1?action=unlock", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ passcode: trimmedPasscode, next: destination }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(payload.error || "The reviewer passcode is not valid.");
        setStatus("ready");
        return;
      }

      openDestination(payload.next || destination);
    } catch {
      setError("The reviewer workspace could not be unlocked. Please try again.");
      setStatus("ready");
    }
  };

  return (
    <>
      <Helmet>
        <title>MANIL-G1 Private Engineering Review | SALAR Technologies</title>
        <meta
          name="description"
          content="Passcode-protected SALAR Technologies reviewer workspace."
        />
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        <meta
          name="googlebot"
          content="noindex, nofollow, noarchive, nosnippet"
        />
      </Helmet>

      <main className="manil-gateway">
        <div className="manil-grid-bg" aria-hidden="true" />
        <section className="manil-gateway-inner">
          <header className="manil-gateway-meta">
            <span className="manil-pill manil-pill--accent">
              <LockKeyhole size={15} aria-hidden="true" />
              Passcode-protected review
            </span>
            <span className="manil-pill">Controlled reviewer access</span>
          </header>

          <div className="manil-gateway-layout">
            <div>
              <p className="manil-overline">SALAR Technologies LLC</p>
              <h1>
                MANIL-G1
                <span>Private engineering review</span>
              </h1>
              <p className="manil-gateway-lede">
                Enter the passcode supplied by SALAR to open the controlled
                workspace and authorized review materials.
              </p>

              <div className="manil-access-grid">
                <AccessCard icon={ShieldCheck} title="Controlled workspace">
                  Review content is released only after server-side session
                  verification.
                </AccessCard>
                <AccessCard icon={FileText} title="Protected materials">
                  Documents, images and data use authenticated SALAR-domain
                  delivery.
                </AccessCard>
                <AccessCard icon={KeyRound} title="Time-limited access">
                  The protected session expires automatically and can be ended
                  at any time.
                </AccessCard>
              </div>

              <aside className="manil-privacy-note">
                <EyeOff size={19} aria-hidden="true" />
                <p>
                  This URL is unlisted and excluded from public navigation.
                  Search indexing and normal caching are disabled. Every document,
                  image and data request requires a valid reviewer session.
                </p>
              </aside>
            </div>

            <div className="manil-access-panel">
              <span className="manil-icon-box manil-icon-box--large">
                <KeyRound size={25} aria-hidden="true" />
              </span>
              <h2>Reviewer access</h2>
              <p>
                Enter the passcode from SALAR. A successful entry creates a
                time-limited protected session.
              </p>

              <form onSubmit={submit} noValidate>
                <label htmlFor="manil-passcode">Passcode</label>
                <input
                  id="manil-passcode"
                  name="passcode"
                  type="password"
                  autoComplete="current-password"
                  value={passcode}
                  onChange={(event) => setPasscode(event.target.value)}
                  disabled={status === "submitting"}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "manil-passcode-error" : undefined}
                  placeholder="Enter reviewer passcode"
                />

                {error ? (
                  <p id="manil-passcode-error" className="manil-form-error">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="manil-button manil-button--primary manil-button--wide"
                  disabled={status === "submitting"}
                >
                  {status === "submitting"
                    ? "Verifying access…"
                    : "Open engineering workspace"}
                  <ArrowRight size={17} aria-hidden="true" />
                </button>
              </form>

              <div className="manil-session-note">
                <ShieldCheck size={17} aria-hidden="true" />
                <span>Protected content is served only after session validation.</span>
              </div>
            </div>
          </div>

          <footer className="manil-gateway-footer">
            <span>UNCLASSIFIED // COMPANY PROPRIETARY // CONTROLLED REVIEW</span>
            <span>
              Vikrant Salar · Founder &amp; Principal ·
              vikrant@salartechnologies.com
            </span>
          </footer>
        </section>
      </main>
    </>
  );
}
